import React from 'react';
import theme from '@common/theme';
import styled from 'styled-components';

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, Command, selectCommandByNodeId, State, tabActions } from '@store/index';
import { IconButton, Stack } from '@mui/material';
import { Button } from '@common/index';
import { CancelOutlined, Clear, DeleteOutlineOutlined, LibraryAddOutlined } from '@mui/icons-material';
import { Resource } from '@store/types';
import { NodeIcon, NodeId, getIcon } from '@common/Node';
import { useTheme } from '../../../styles';
import { useParams } from 'react-router-dom';
import { usePermissions } from '@common/hooks/usePermissions';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  color: ${({ theme }) => theme.text.standard};
  background-color: ${({ theme }) => theme.background.header};
  height: 62px;
  border-top: 1px solid ${({ theme }) => theme.line.light};
  padding: 0 ${theme.spacing(2.5)};
  p {
    margin: 0;
    display: flex;
    align-items: center;
  }
  .flex {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-weight: 500;
  }
`;

export const BottomToolbar: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const { service_id } = useParams();
  const { admin, permissions } = usePermissions(service_id as string, Resource.WORKFLOW);

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  const edges = useSelector((state: State) => state.tabs.active.edges);
  const themeType = useSelector((state: State) => state.flow.theme);

  const selectedNode = useSelector((state: State) => state.tabs.active.selectedNode);
  const selected = commands.find((command: Command) => command.node.id === selectedNode);
  const selectedEdges = edges.filter((edge) => edge.source === selected?.node.id || edge.target === selected?.node.id);

  const isCompletion = command?.node.type === 'Completion';
  const background = isCompletion && themeType === 'dark' ? '#5858FF' : command?.config?.background;

  const icon = command?.config?.icon;
  const allowDelete = admin || permissions?.delete;

  return (
    <Container>
      <div className='flex'>
        <NodeId background={background}>#{command?.node?.id}</NodeId>
        <NodeIcon background={background}>{getIcon(icon)}</NodeIcon>
        {selected?.config.title || 'Unknown'}
      </div>
      <Stack spacing={1.5} direction='row' alignItems='center'>
        <Button
          small
          disabled={command?.config?.trigger}
          onClick={() => dispatch(tabActions.duplicateNode(selected?.node.id || ''))}
          startIcon={<LibraryAddOutlined sx={{ color: theme.text.subtext }} />}
        >
          Duplicate
        </Button>
        <Button
          small
          disabled={selectedEdges?.length === 0 || !allowDelete}
          startIcon={<CancelOutlined sx={{ color: theme.text.subtext }} />}
          onClick={() => dispatch(tabActions.removeEdges(selected?.node.id || ''))}
        >
          Remove Edges
        </Button>
        <Button
          small
          onClick={() => {
            dispatch(tabActions.removeNode(selected?.node.id as string));
          }}
          startIcon={<DeleteOutlineOutlined sx={{ color: theme.text.subtext }} />}
          disabled={!allowDelete}
        >
          Delete Node
        </Button>
        <IconButton onClick={() => dispatch(tabActions.setSelectedNode(null))} sx={{ borderRadius: '6px' }}>
          <Clear sx={{ fontSize: '1.3ch', color: theme.text.standard }} />
        </IconButton>
      </Stack>
    </Container>
  );
};
